import { useCallback } from 'react'
import { ITranslationKey, T } from '@/components/t'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { IChildrenProps } from '@/typings/children-props'
import { cn } from '@/utils/styles/cn'

interface IRestaurantDetailSectionProps extends IChildrenProps {
    title: ITranslationKey
    description?: ITranslationKey
    id: string | null
    className?: string
    withoutBorder?: boolean
    innerContainerClassName?: string
    titleClassName?: string
}

export const RestaurantDetailSection = ({
    children,
    title,
    description,
    id,
    className,
    withoutBorder = false,
    titleClassName,
    innerContainerClassName,
}: IRestaurantDetailSectionProps) => {
    const { registerSection } = useRestaurantDetailContext()

    const createRef = useCallback(
        // Register the section in the context, so that we can use it in the navigation (e.g. scroll to section on click)
        (el: HTMLDivElement) => (id ? registerSection(id, el) : undefined),
        [registerSection, id]
    )

    return (
        <div
            ref={createRef}
            className={cn(
                'flex flex-col w-full pb-10 md:pb-14 mb-10 md:mb-14',
                !withoutBorder && 'border-b-[1px] border-b-gray-200',
                className
            )}
        >
            <div className={cn('flex flex-col w-full mb-5 md:px-0 px-1', innerContainerClassName)}>
                <T
                    h2
                    t={title}
                    className={cn(
                        'text-[20px] md:text-[24px] font-semibold',
                        !!description && 'mb-2',
                        titleClassName
                    )}
                />

                {description && (
                    <T p t={description} className="text-gray-500 text-[14px] md:text-[16px]" />
                )}
            </div>

            {children}
        </div>
    )
}
